import { isSupportedLanguage } from '../utils/i18n.js';

/**
 * The desktop-session identity that rides on the auth pages (kingcad#426).
 *
 * The KingCAD desktop clients open /register, /login and /forgot-password in
 * the user's browser with `?client=<id>&lang=<code>[&email=<addr>]`. Both of
 * those have to survive every hop the user makes between the auth pages —
 * register ⇄ login ⇄ forgot-password ⇄ verify-email — or the second page
 * reverts to the OAuth-carrying web layout in the wrong language.
 */

/**
 * Desktop client id → the product name shown to the user.
 *
 * The ids are the ones the desktop side actually sends
 * (`SewerApp::GetAppId()`, `g4cApp::GetAppID()`, and the hardcoded `d96`);
 * `g4c` and `prostor` are the same product under its two identifiers.
 */
export const DESKTOP_CLIENT_PRODUCTS: Record<string, string> = {
  sewer: 'Sewer+',
  g4c: 'King.Prostor',
  prostor: 'King.Prostor',
  d96: 'King.D96',
};

/**
 * The `?client=` value, but only when it names a known desktop client.
 *
 * An unknown value yields '' so the page renders as the plain web variant
 * rather than a half-desktop one. `hasOwnProperty` rather than a truthiness
 * check on the lookup: a `?client=constructor` would otherwise resolve through
 * Object.prototype.
 */
export function readDesktopClient(search: string = window.location.search): string {
  const value = new URLSearchParams(search).get('client') ?? '';
  return Object.prototype.hasOwnProperty.call(DESKTOP_CLIENT_PRODUCTS, value) ? value : '';
}

/** Product name for a validated client id, or '' when there is none. */
export function desktopProductName(client: string): string {
  return DESKTOP_CLIENT_PRODUCTS[client] ?? '';
}

/**
 * Copy `lang` and `client` from the current URL onto an outgoing auth link.
 *
 * Only an EXPLICIT `?lang=` is forwarded, not the language detection result:
 * a locally detected preference already survives the hop through localStorage,
 * whereas the desktop client's pinned language exists nowhere but the URL, and
 * appending a detected default to every link would put `?lang=sl` on links
 * shared out of the web app.
 */
export function carryDesktopParams(
  params: URLSearchParams,
  search: string = window.location.search
): URLSearchParams {
  const urlParams = new URLSearchParams(search);

  const lang = urlParams.get('lang');
  if (isSupportedLanguage(lang)) {
    params.set('lang', lang);
  }

  const client = readDesktopClient(search);
  if (client) {
    params.set('client', client);
  }

  return params;
}
